/** In-memory cache for Supabase signed media URLs (keyed by bucket object path). */

const cache = new Map();
const inflight = new Map();

const REFRESH_BUFFER_MS = 5 * 60 * 1000;

function parseSignedUrlExpiryMs(signedUrl) {
    try {
        const token = new URL(signedUrl).searchParams.get('token');
        if (!token) return null;
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (payload?.exp) return payload.exp * 1000;
    } catch {
        /* ignore malformed token */
    }
    return null;
}

function isCacheValid(entry) {
    return entry && entry.expiresAtMs > Date.now() + REFRESH_BUFFER_MS;
}

/** True when a storage signed URL token is still valid (default 5 min buffer). */
export function isSignedStorageUrlValid(url, bufferMs = REFRESH_BUFFER_MS) {
    const expiresAtMs = parseSignedUrlExpiryMs(url);
    if (!expiresAtMs) return false;
    return expiresAtMs > Date.now() + bufferMs;
}

/**
 * Return a cached signed URL or fetch a new one. Deduplicates concurrent requests per path.
 * @param {string} filePath - Object path inside the `media` bucket
 * @param {(path: string) => Promise<string>} fetchSignedUrl
 * @param {{ bypassCache?: boolean }} [options]
 */
export async function getCachedSignedMediaUrl(filePath, fetchSignedUrl, options = {}) {
    if (!filePath) return null;

    if (!options.bypassCache) {
        const cached = cache.get(filePath);
        if (isCacheValid(cached)) return cached.url;
    }

    if (!options.bypassCache && inflight.has(filePath)) {
        return inflight.get(filePath);
    }

    const promise = (async () => {
        try {
            const url = await fetchSignedUrl(filePath);
            if (url) {
                const expiresAtMs = parseSignedUrlExpiryMs(url) || (Date.now() + 23 * 60 * 60 * 1000);
                cache.set(filePath, { url, expiresAtMs });
            }
            return url;
        } finally {
            inflight.delete(filePath);
        }
    })();

    inflight.set(filePath, promise);
    return promise;
}

export function clearSignedMediaUrlCache() {
    cache.clear();
    inflight.clear();
}
